import React, { useState, useEffect, useCallback } from 'react';
import DashboardWrapper from '../../components/DashboardWrapper';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Plus, Search, Edit, Trash2, Package, Calendar, GripVertical,
  ChevronUp, ChevronDown, X, LayoutGrid, Eye, EyeOff, Archive,
  Clock, CheckCircle, FileEdit, ArrowUpDown, AlertTriangle, Info, ShieldAlert,
  BarChart3, CalendarDays, ChevronLeft, ChevronRight, Layers, Target
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

// ════════════════════════════════════════════════════════
// STATUS CONFIG
// ════════════════════════════════════════════════════════
const STATUS_CONFIG = {
  draft:     { label: 'Brouillon',  icon: FileEdit,    color: 'bg-gray-100 text-gray-700 border-gray-300' },
  scheduled: { label: 'Planifiée',  icon: Clock,       color: 'bg-blue-100 text-blue-700 border-blue-300' },
  active:    { label: 'Active',     icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  archived:  { label: 'Archivée',   icon: Archive,     color: 'bg-orange-100 text-orange-700 border-orange-300' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.color}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
};

// Display reason labels (derived state — read-only, never mutates status)
const DISPLAY_REASON_CONFIG = {
  resolved:  { label: 'Affichée',               color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Eye },
  priority:  { label: 'Masquée (priorité)',      color: 'bg-amber-50 text-amber-700 border-amber-200',      icon: EyeOff },
  empty:     { label: 'Masquée (0 produits)',     color: 'bg-red-50 text-red-700 border-red-200',            icon: AlertTriangle },
  draft:     { label: 'Non publiée',             color: 'bg-gray-50 text-gray-500 border-gray-200',         icon: FileEdit },
  scheduled: { label: 'Programmée',              color: 'bg-blue-50 text-blue-600 border-blue-200',         icon: Clock },
  archived:  { label: 'Expirée',                 color: 'bg-orange-50 text-orange-600 border-orange-200',   icon: Archive },
  inactive:  { label: 'Inactive',                color: 'bg-gray-50 text-gray-500 border-gray-200',         icon: EyeOff },
};

const DisplayBadge = ({ reason }) => {
  const cfg = DISPLAY_REASON_CONFIG[reason] || DISPLAY_REASON_CONFIG.inactive;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.color}`} data-testid={`display-badge-${reason}`}>
      <Icon className="h-2.5 w-2.5" />
      {cfg.label}
    </span>
  );
};

// ════════════════════════════════════════════════════════
// TIMELINE VIEW
// ════════════════════════════════════════════════════════
const STATUS_COLORS = {
  active: { bg: '#d1fae5', border: '#6ee7b7', text: '#065f46' },
  scheduled: { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
  draft: { bg: '#f3f4f6', border: '#d1d5db', text: '#4b5563' },
  archived: { bg: '#fed7aa', border: '#fdba74', text: '#9a3412' },
};

const TimelineView = ({ vitrines, placements, onEditVitrine }) => {
  const [monthOffset, setMonthOffset] = useState(0);

  // Compute visible month range (current month + offset, show 3 months)
  const baseDate = new Date();
  baseDate.setMonth(baseDate.getMonth() + monthOffset);
  const startMonth = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  const endMonth = new Date(baseDate.getFullYear(), baseDate.getMonth() + 3, 0);

  const rangeStartISO = startMonth.toISOString().split('T')[0];
  const rangeEndISO = endMonth.toISOString().split('T')[0];
  const totalDays = Math.ceil((endMonth - startMonth) / (1000 * 60 * 60 * 24)) + 1;

  const monthLabels = [];
  for (let i = 0; i < 3; i++) {
    const m = new Date(startMonth.getFullYear(), startMonth.getMonth() + i, 1);
    const daysInMonth = new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate();
    const daysBefore = Math.ceil((m - startMonth) / (1000 * 60 * 60 * 24));
    monthLabels.push({
      label: m.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
      left: (daysBefore / totalDays) * 100,
      width: (daysInMonth / totalDays) * 100,
    });
  }

  // Today marker position
  const todayISO = new Date().toISOString().split('T')[0];
  const todayOffset = Math.ceil((new Date(todayISO) - startMonth) / (1000 * 60 * 60 * 24));
  const todayPct = (todayOffset / totalDays) * 100;
  const showToday = todayPct >= 0 && todayPct <= 100;

  // Group vitrines by placement
  const grouped = {};
  placements.forEach(p => { grouped[p.key] = { ...p, vitrines: [] }; });
  vitrines.forEach(v => {
    if (grouped[v.placement_key]) {
      grouped[v.placement_key].vitrines.push(v);
    }
  });

  const getBarPosition = (v) => {
    const s = v.start_date || rangeStartISO;
    const e = v.end_date || rangeEndISO;
    const startDay = Math.max(0, Math.ceil((new Date(s) - startMonth) / (1000 * 60 * 60 * 24)));
    const endDay = Math.min(totalDays, Math.ceil((new Date(e) - startMonth) / (1000 * 60 * 60 * 24)) + 1);
    if (endDay <= 0 || startDay >= totalDays) return null;
    return {
      left: (Math.max(0, startDay) / totalDays) * 100,
      width: ((endDay - Math.max(0, startDay)) / totalDays) * 100,
    };
  };

  const placementKeys = Object.keys(grouped);

  return (
    <div className="space-y-4" data-testid="vitrines-timeline-view">
      {/* Navigation */}
      <div className="flex items-center justify-between bg-white border rounded-lg px-4 py-2">
        <Button variant="ghost" size="sm" onClick={() => setMonthOffset(o => o - 1)} data-testid="timeline-prev">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex gap-6">
          {monthLabels.map((m, i) => (
            <span key={i} className="text-sm font-medium text-gray-700 capitalize">{m.label}</span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setMonthOffset(0)} className="text-xs" data-testid="timeline-today">Aujourd{"'"}hui</Button>
          <Button variant="ghost" size="sm" onClick={() => setMonthOffset(o => o + 1)} data-testid="timeline-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Timeline grid */}
      <div className="bg-white border rounded-lg overflow-hidden">
        {/* Month header */}
        <div className="relative h-8 border-b bg-gray-50" data-testid="timeline-header">
          {monthLabels.map((m, i) => (
            <div key={i} className="absolute top-0 h-full flex items-center border-l border-gray-200 first:border-l-0" style={{ left: `${m.left}%`, width: `${m.width}%` }}>
              <span className="text-[11px] font-medium text-gray-500 pl-2 capitalize truncate">{m.label}</span>
            </div>
          ))}
          {showToday && (
            <div className="absolute top-0 h-full w-px bg-red-400 z-20" style={{ left: `${todayPct}%` }} title="Aujourd'hui">
              <div className="absolute -top-0 -translate-x-1/2 bg-red-500 text-white text-[9px] font-bold px-1 rounded-b">Auj.</div>
            </div>
          )}
        </div>

        {/* Placement rows */}
        {placementKeys.map(pk => {
          const group = grouped[pk];
          const visibleVitrines = group.vitrines.filter(v => {
            const pos = getBarPosition(v);
            return pos !== null;
          });

          return (
            <div key={pk} className="flex border-b last:border-b-0 min-h-[48px]" data-testid={`timeline-row-${pk}`}>
              {/* Label */}
              <div className="w-48 flex-shrink-0 px-3 py-2 bg-gray-50 border-r flex items-start">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-700 truncate">{group.label}</p>
                  <p className="text-[10px] text-gray-400">{group.vitrines.length} vitrine{group.vitrines.length > 1 ? 's' : ''}</p>
                </div>
              </div>
              {/* Bars */}
              <div className="flex-1 relative py-1.5 min-h-[48px]">
                {/* Month grid lines */}
                {monthLabels.map((m, i) => i > 0 && (
                  <div key={i} className="absolute top-0 bottom-0 w-px bg-gray-100" style={{ left: `${m.left}%` }} />
                ))}
                {showToday && <div className="absolute top-0 bottom-0 w-px bg-red-300/50 z-10" style={{ left: `${todayPct}%` }} />}
                
                {visibleVitrines.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[10px] text-gray-300">—</span>
                  </div>
                )}
                {visibleVitrines.map((v, vIdx) => {
                  const pos = getBarPosition(v);
                  if (!pos) return null;
                  const colors = STATUS_COLORS[v.effective_status] || STATUS_COLORS.draft;
                  const isDisplayed = v.is_displayed;
                  return (
                    <button
                      key={v.id}
                      onClick={() => onEditVitrine(v)}
                      title={`${v.title} (${v.effective_status})\n${v.start_date || '∞'} → ${v.end_date || '∞'}\n${v.product_count} produit(s)`}
                      className="absolute h-7 rounded-md border text-[10px] font-medium px-1.5 truncate cursor-pointer hover:brightness-95 transition-all flex items-center gap-1"
                      style={{
                        left: `${pos.left}%`,
                        width: `${Math.max(pos.width, 2)}%`,
                        top: `${4 + vIdx * 32}px`,
                        backgroundColor: colors.bg,
                        borderColor: colors.border,
                        color: colors.text,
                        opacity: isDisplayed ? 1 : (v.effective_status === 'draft' ? 0.5 : 0.7),
                        minWidth: '24px',
                      }}
                      data-testid={`timeline-bar-${v.id}`}
                    >
                      {isDisplayed && <Eye className="h-2.5 w-2.5 flex-shrink-0" />}
                      <span className="truncate">{v.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-500 px-1">
        {Object.entries(STATUS_COLORS).map(([s, c]) => (
          <div key={s} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm border" style={{ backgroundColor: c.bg, borderColor: c.border }} />
            {STATUS_CONFIG[s]?.label || s}
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-px bg-red-400" />
          Aujourd{"'"}hui
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════
// DASHBOARD VIEW
// ════════════════════════════════════════════════════════
const DashboardView = ({ planningData }) => {
  if (!planningData) return null;
  const { kpi, placements: placementGroups } = planningData;

  return (
    <div className="space-y-6" data-testid="vitrines-dashboard-view">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { key: 'total', label: 'Total vitrines', value: kpi.total, icon: LayoutGrid, color: 'text-gray-600' },
          { key: 'actives', label: 'Actives', value: kpi.active, icon: CheckCircle, color: 'text-emerald-600' },
          { key: 'scheduled', label: 'Planifiées', value: kpi.scheduled, icon: Clock, color: 'text-blue-600' },
          { key: 'draft', label: 'Brouillons', value: kpi.draft, icon: FileEdit, color: 'text-gray-400' },
          { key: 'archived', label: 'Archivées', value: kpi.archived, icon: Archive, color: 'text-orange-500' },
        ].map(({ key, label, value, icon: Icon, color }) => (
          <div key={key} className="bg-white border rounded-lg p-4 flex items-center gap-3" data-testid={`kpi-${key}`}>
            <Icon className={`h-5 w-5 ${color}`} />
            <div>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Alerts */}
      {(kpi.conflict_placements > 0 || kpi.empty_active > 0) && (
        <div className="space-y-2" data-testid="dashboard-alerts">
          {kpi.conflict_placements > 0 && (
            <div className="flex items-center gap-2 p-3 rounded-lg border bg-red-50 border-red-200 text-red-800 text-sm">
              <ShieldAlert className="h-4 w-4 flex-shrink-0" />
              <span><strong>{kpi.conflict_placements}</strong> emplacement(s) avec conflit — plusieurs vitrines actives sur le même emplacement</span>
            </div>
          )}
          {kpi.empty_active > 0 && (
            <div className="flex items-center gap-2 p-3 rounded-lg border bg-amber-50 border-amber-200 text-amber-800 text-sm">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span><strong>{kpi.empty_active}</strong> vitrine(s) active(s) sans produits</span>
            </div>
          )}
        </div>
      )}

      {/* Placement Occupancy */}
      <div className="bg-white border rounded-lg">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <Target className="h-4 w-4 text-gray-400" />
            Occupation des emplacements ({kpi.occupied_placements}/{kpi.total_placements})
          </h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(planningData.placement_registry || []).map(pr => {
              const group = placementGroups.find(g => g.placement_key === pr.key);
              const activeCount = group?.active_count || 0;
              const scheduledCount = group?.scheduled_count || 0;
              const total = group?.vitrines?.length || 0;
              const hasConflict = group?.has_conflict;
              const isOccupied = activeCount > 0;

              return (
                <div key={pr.key} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${hasConflict ? 'border-red-200 bg-red-50/50' : isOccupied ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200 bg-white'}`}>
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${hasConflict ? 'bg-red-500' : isOccupied ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{pr.label}</p>
                    <div className="flex items-center gap-2 text-[11px] text-gray-500">
                      {activeCount > 0 && <span className="text-emerald-600 font-medium">{activeCount} active{activeCount > 1 ? 's' : ''}</span>}
                      {scheduledCount > 0 && <span className="text-blue-600">{scheduledCount} planifiée{scheduledCount > 1 ? 's' : ''}</span>}
                      {total === 0 && <span className="text-gray-400">Vacant</span>}
                      {hasConflict && <span className="text-red-600 font-semibold">Conflit</span>}
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 font-mono">{total}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════
// PRODUCT SELECTOR
// ════════════════════════════════════════════════════════
const ProductSelector = ({ selectedIds, onAdd, onRemove, onReorder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searching, setSearching] = useState(false);
  const [noResults, setNoResults] = useState(false);

  // Load selected products details by IDs
  useEffect(() => {
    if (selectedIds.length === 0) { setSelectedProducts([]); return; }
    const loadProducts = async () => {
      try {
        const res = await api.get(`/products?ids=${selectedIds.join(',')}&limit=${selectedIds.length}`);
        const all = Array.isArray(res.data) ? res.data : (res.data?.products || []);
        const idOrder = {};
        selectedIds.forEach((id, idx) => { idOrder[id] = idx; });
        all.sort((a, b) => (idOrder[a.id] ?? 999) - (idOrder[b.id] ?? 999));
        setSelectedProducts(all);
      } catch { /* silent */ }
    };
    loadProducts();
  }, [selectedIds]);

  // Debounced search
  useEffect(() => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      setNoResults(false);
      return;
    }
    setSearching(true);
    setNoResults(false);
    const timeout = setTimeout(async () => {
      try {
        const res = await api.get(`/products?search=${encodeURIComponent(searchTerm)}&limit=15`);
        const all = Array.isArray(res.data) ? res.data : (res.data?.products || []);
        const filtered = all.filter(p => !selectedIds.includes(p.id));
        setSearchResults(filtered);
        setNoResults(all.length > 0 && filtered.length === 0 ? 'already_selected' : (all.length === 0 ? 'no_match' : false));
      } catch { setSearchResults([]); setNoResults('error'); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(timeout);
  }, [searchTerm, selectedIds]);

  const moveProduct = (idx, direction) => {
    const newIds = [...selectedIds];
    const target = idx + direction;
    if (target < 0 || target >= newIds.length) return;
    [newIds[idx], newIds[target]] = [newIds[target], newIds[idx]];
    onReorder(newIds);
  };

  return (
    <div className="space-y-4">
      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher un produit par nom..."
          className="pl-10"
          data-testid="vitrine-product-search"
        />
        {searching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
          </div>
        )}
      </div>

      {/* Search results */}
      {searchResults.length > 0 && (
        <div className="border rounded-lg max-h-48 overflow-y-auto bg-white divide-y" data-testid="vitrine-search-results">
          {searchResults.map(p => (
            <button
              key={p.id}
              onClick={() => { onAdd(p.id); setSearchTerm(''); setSearchResults([]); }}
              className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-sm transition-colors"
              data-testid={`vitrine-add-product-${p.id}`}
            >
              {p.visuels?.[0] ? (
                <img src={p.visuels[0]} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Package className="h-4 w-4 text-gray-400" />
                </div>
              )}
              <span className="font-medium text-gray-800 truncate">{p.intitule}</span>
              {(() => { const pub = (p.original_publishers || p.publishers || []).map(x => x.name).filter(Boolean).join(' / ') || p.editeur; return pub ? <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{pub}</span> : null; })()}
              <Plus className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
      {searching && <p className="text-xs text-gray-400 text-center" data-testid="vitrine-search-loading">Recherche...</p>}
      {noResults === 'no_match' && !searching && (
        <p className="text-xs text-gray-400 text-center py-2" data-testid="vitrine-search-no-match">Aucun produit trouvé pour &laquo; {searchTerm} &raquo;</p>
      )}
      {noResults === 'already_selected' && !searching && (
        <p className="text-xs text-amber-500 text-center py-2" data-testid="vitrine-search-already-selected">Tous les résultats sont déjà dans la vitrine</p>
      )}

      {/* Selected products list */}
      <div className="space-y-1" data-testid="vitrine-selected-products">
        {selectedProducts.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm border border-dashed rounded-lg">
            <Package className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            Aucun produit sélectionné
          </div>
        )}
        {selectedProducts.map((p, idx) => (
          <div
            key={p.id}
            className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg group hover:border-gray-300 transition-colors"
            data-testid={`vitrine-product-row-${idx}`}
          >
            <span className="text-xs text-gray-400 w-5 text-center font-mono">{idx + 1}</span>
            <GripVertical className="h-4 w-4 text-gray-300 flex-shrink-0" />
            {p.visuels?.[0] ? (
              <img src={p.visuels[0]} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Package className="h-3.5 w-3.5 text-gray-400" />
              </div>
            )}
            <span className="text-sm font-medium text-gray-800 truncate flex-1">{p.intitule}</span>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => moveProduct(idx, -1)} disabled={idx === 0}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30" title="Monter">
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => moveProduct(idx, 1)} disabled={idx === selectedProducts.length - 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30" title="Descendre">
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => onRemove(p.id)}
                className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500" title="Retirer">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════
const AdminVitrines = () => {
  const [vitrines, setVitrines] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlacement, setFilterPlacement] = useState('all');
  const [activeTab, setActiveTab] = useState('list');
  const [planningData, setPlanningData] = useState(null);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVitrine, setEditingVitrine] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    placement_key: '',
    start_date: '',
    end_date: '',
    priority: 0,
    status: 'draft',
    product_ids: [],
    max_products: 20,
    columns: 4,
    rows: 1,
  });

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Conflict Guard state
  const [conflicts, setConflicts] = useState({ status: 'ok', conflicts: [] });
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  useEffect(() => { loadData(); }, []);

  // ── Conflict check (debounced on form changes) ──
  useEffect(() => {
    if (!modalOpen) { setConflicts({ status: 'ok', conflicts: [] }); return; }
    if (!formData.placement_key || !formData.status) return;

    const timeout = setTimeout(async () => {
      setCheckingConflicts(true);
      try {
        const payload = {
          ...formData,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          _exclude_id: editingVitrine?.id || null,
        };
        const res = await api.post('/vitrines/check-conflicts', payload);
        setConflicts(res.data || { status: 'ok', conflicts: [] });
      } catch { setConflicts({ status: 'ok', conflicts: [] }); }
      finally { setCheckingConflicts(false); }
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, formData.placement_key, formData.status, formData.product_ids?.length, formData.start_date, formData.end_date, formData.priority]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [vitRes, plRes, planRes] = await Promise.all([
        api.get('/vitrines'),
        api.get('/vitrines/placements'),
        api.get('/vitrines/planning'),
      ]);
      setVitrines(vitRes.data || []);
      setPlacements(plRes.data || []);
      setPlanningData(planRes.data || null);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingVitrine(null);
    setFormData({
      title: '', description: '', placement_key: '',
      start_date: '', end_date: '', priority: 0,
      status: 'draft', product_ids: [], max_products: 20,
      columns: 4, rows: 1,
    });
    setModalOpen(true);
  };

  const openEditModal = (vitrine) => {
    setEditingVitrine(vitrine);
    setFormData({
      title: vitrine.title || '',
      description: vitrine.description || '',
      placement_key: vitrine.placement_key || '',
      start_date: vitrine.start_date || '',
      end_date: vitrine.end_date || '',
      priority: vitrine.priority ?? 0,
      status: vitrine.status || 'draft',
      product_ids: vitrine.product_ids || [],
      max_products: vitrine.max_products ?? 20,
      columns: vitrine.columns ?? 4,
      rows: vitrine.rows ?? 1,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) { toast.error('Le titre est obligatoire'); return; }
    if (!formData.placement_key) { toast.error("L'emplacement est obligatoire"); return; }
    
    // Block save on critical conflicts
    if (conflicts.status === 'error') {
      const critical = conflicts.conflicts.find(c => c.level === 'critical');
      toast.error(critical?.message || 'Configuration invalide — corrigez les erreurs avant de sauvegarder');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
      };

      if (editingVitrine) {
        await api.put(`/vitrines/${editingVitrine.id}`, payload);
        toast.success('Vitrine mise à jour');
      } else {
        await api.post('/vitrines', payload);
        toast.success('Vitrine créée');
      }
      setModalOpen(false);
      loadData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'object' && detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error(detail || 'Erreur lors de la sauvegarde');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/vitrines/${id}`);
      toast.success('Vitrine supprimée');
      setDeleteConfirm(null);
      loadData();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleQuickStatusToggle = async (vitrine) => {
    // Explicit status change — NOT a display toggle
    const newStatus = vitrine.status === 'active' ? 'draft' : 'active';
    const label = newStatus === 'active' ? 'Activer' : 'Passer en brouillon';
    try {
      await api.put(`/vitrines/${vitrine.id}`, { status: newStatus });
      toast.success(`Vitrine : ${label}`);
      loadData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'object' && detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error(detail || 'Erreur');
      }
    }
  };

  // Filters
  const filteredVitrines = vitrines.filter(v => {
    if (filterStatus !== 'all' && v.effective_status !== filterStatus) return false;
    if (filterPlacement !== 'all' && v.placement_key !== filterPlacement) return false;
    if (searchTerm && !v.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return d; }
  };

  // ════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════
  return (
    <DashboardWrapper>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="admin-vitrines-page">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="vitrines-title">
              <LayoutGrid className="h-6 w-6 text-gray-400" />
              Vitrines Marketing
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Gérez vos sélections éditoriales de produits
            </p>
          </div>
          <Button onClick={openCreateModal} className="gap-2" data-testid="create-vitrine-btn">
            <Plus className="h-4 w-4" /> Nouvelle vitrine
          </Button>
        </div>

        {/* View tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="h-10 p-1 bg-gray-100 border rounded-lg w-auto inline-flex" data-testid="vitrines-view-tabs">
            <TabsTrigger value="list" className="rounded-md px-4 text-sm font-medium gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Layers className="h-3.5 w-3.5" /> Liste
            </TabsTrigger>
            <TabsTrigger value="timeline" className="rounded-md px-4 text-sm font-medium gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-timeline">
              <CalendarDays className="h-3.5 w-3.5" /> Planning
            </TabsTrigger>
            <TabsTrigger value="dashboard" className="rounded-md px-4 text-sm font-medium gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-dashboard">
              <BarChart3 className="h-3.5 w-3.5" /> Dashboard
            </TabsTrigger>
          </TabsList>

          {/* ═══ TAB: LIST ═══ */}
          <TabsContent value="list" className="mt-4 space-y-6">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center bg-white border rounded-lg p-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher une vitrine..."
              className="pl-10"
              data-testid="vitrines-search"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[160px]" data-testid="vitrines-filter-status">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="scheduled">Planifiée</SelectItem>
              <SelectItem value="draft">Brouillon</SelectItem>
              <SelectItem value="archived">Archivée</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterPlacement} onValueChange={setFilterPlacement}>
            <SelectTrigger className="w-[200px]" data-testid="vitrines-filter-placement">
              <SelectValue placeholder="Emplacement" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les emplacements</SelectItem>
              {placements.map(p => (
                <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {['active', 'scheduled', 'draft', 'archived'].map(s => {
            const count = vitrines.filter(v => v.effective_status === s).length;
            const cfg = STATUS_CONFIG[s];
            const Icon = cfg.icon;
            return (
              <button key={s} onClick={() => setFilterStatus(filterStatus === s ? 'all' : s)}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${filterStatus === s ? 'ring-2 ring-gray-900 border-gray-900' : 'bg-white border-gray-200 hover:border-gray-300'}`}
                data-testid={`vitrines-stat-${s}`}
              >
                <Icon className="h-5 w-5 text-gray-400" />
                <div className="text-left">
                  <p className="text-xl font-bold text-gray-900">{count}</p>
                  <p className="text-xs text-gray-500">{cfg.label}{count > 1 ? 's' : ''}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Vitrines list */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto" />
          </div>
        ) : filteredVitrines.length === 0 ? (
          <div className="text-center py-16 bg-white border rounded-lg">
            <LayoutGrid className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Aucune vitrine trouvée</p>
            <p className="text-sm text-gray-400 mt-1">Créez votre première vitrine marketing</p>
            <Button onClick={openCreateModal} variant="outline" className="mt-4 gap-2">
              <Plus className="h-4 w-4" /> Créer une vitrine
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="vitrines-list">
            {filteredVitrines.map((v) => (
              <div
                key={v.id}
                className="bg-white border rounded-lg p-4 hover:border-gray-300 transition-colors group"
                data-testid={`vitrine-row-${v.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {/* Main info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 truncate">{v.title}</h3>
                      <StatusBadge status={v.effective_status} />
                      <DisplayBadge reason={v.display_reason} />
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <LayoutGrid className="h-3 w-3" /> {v.placement_label}
                      </span>
                      <span className="flex items-center gap-1">
                        <Package className="h-3 w-3" /> {v.product_count} produit{v.product_count > 1 ? 's' : ''}
                      </span>
                      {(v.start_date || v.end_date) && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {v.start_date ? formatDate(v.start_date) : '...'} → {v.end_date ? formatDate(v.end_date) : '...'}
                        </span>
                      )}
                      {v.priority > 0 && (
                        <span className="flex items-center gap-1">
                          <ArrowUpDown className="h-3 w-3" /> Priorité {v.priority}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions — STATUS controls (never mutate display state) */}
                  <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                    {v.status === 'active' ? (
                      <Button size="sm" variant="ghost" onClick={() => handleQuickStatusToggle(v)}
                        className="h-8 px-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50"
                        data-testid={`vitrine-toggle-${v.id}`}
                        title="Passer en brouillon (désactiver le statut éditorial)">
                        <FileEdit className="h-4 w-4" />
                      </Button>
                    ) : v.status !== 'archived' ? (
                      <Button size="sm" variant="ghost" onClick={() => handleQuickStatusToggle(v)}
                        className="h-8 px-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50"
                        data-testid={`vitrine-toggle-${v.id}`}
                        title="Activer (publier la vitrine)">
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    ) : null}
                    <Button size="sm" variant="ghost" onClick={() => openEditModal(v)}
                      className="h-8 px-2" data-testid={`vitrine-edit-${v.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(v.id)}
                      className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`vitrine-delete-${v.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

          </TabsContent>

          {/* ═══ TAB: TIMELINE ═══ */}
          <TabsContent value="timeline" className="mt-4">
            {loading ? (
              <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto" /></div>
            ) : (
              <TimelineView vitrines={vitrines} placements={placements} onEditVitrine={openEditModal} />
            )}
          </TabsContent>

          {/* ═══ TAB: DASHBOARD ═══ */}
          <TabsContent value="dashboard" className="mt-4">
            {loading ? (
              <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto" /></div>
            ) : (
              <DashboardView planningData={planningData} />
            )}
          </TabsContent>
        </Tabs>

        {/* ════════════════════════════════════════════ */}
        {/* CREATE / EDIT MODAL */}
        {/* ════════════════════════════════════════════ */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle data-testid="vitrine-modal-title">
                {editingVitrine ? 'Modifier la vitrine' : 'Nouvelle vitrine'}
              </DialogTitle>
              <DialogDescription>
                {editingVitrine ? 'Modifiez les paramètres de cette vitrine marketing.' : 'Créez une nouvelle sélection éditoriale de produits.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 mt-2">
              {/* Title */}
              <div className="space-y-1.5">
                <Label htmlFor="vit-title">Titre *</Label>
                <Input
                  id="vit-title"
                  value={formData.title}
                  onChange={(e) => setFormData(f => ({ ...f, title: e.target.value }))}
                  placeholder="Ex: Sélection Été 2026"
                  data-testid="vitrine-form-title"
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label htmlFor="vit-desc">Description</Label>
                <Textarea
                  id="vit-desc"
                  value={formData.description}
                  onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                  placeholder="Description interne (non affichée au retailer)"
                  rows={2}
                  data-testid="vitrine-form-description"
                />
              </div>

              {/* Placement + Status row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Emplacement marketing *</Label>
                  <Select
                    value={formData.placement_key}
                    onValueChange={(v) => setFormData(f => ({ ...f, placement_key: v }))}
                  >
                    <SelectTrigger data-testid="vitrine-form-placement">
                      <SelectValue placeholder="Choisir un emplacement" />
                    </SelectTrigger>
                    <SelectContent>
                      {placements.map(p => (
                        <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Statut</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData(f => ({ ...f, status: v }))}
                  >
                    <SelectTrigger data-testid="vitrine-form-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Brouillon</SelectItem>
                      <SelectItem value="scheduled">Planifiée</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="archived">Archivée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dates row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="vit-start">Date de début</Label>
                  <Input
                    id="vit-start"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData(f => ({ ...f, start_date: e.target.value }))}
                    data-testid="vitrine-form-start-date"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="vit-end">Date de fin</Label>
                  <Input
                    id="vit-end"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData(f => ({ ...f, end_date: e.target.value }))}
                    data-testid="vitrine-form-end-date"
                  />
                </div>
              </div>

              {/* Priority + Max */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="vit-priority">Priorité d{"'"}affichage</Label>
                  <Input
                    id="vit-priority"
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData(f => ({ ...f, priority: parseInt(e.target.value) || 0 }))}
                    min={0}
                    data-testid="vitrine-form-priority"
                  />
                  <p className="text-xs text-gray-400">0 = priorité maximale</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="vit-cols">Produits par ligne</Label>
                  <Select
                    value={String(formData.columns)}
                    onValueChange={(v) => {
                      const cols = parseInt(v);
                      setFormData(f => ({ ...f, columns: cols, max_products: cols * f.rows }));
                    }}
                  >
                    <SelectTrigger id="vit-cols" data-testid="vitrine-form-columns">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2, 3, 4, 5, 6].map(n => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="vit-rows">Nombre de lignes</Label>
                  <Select
                    value={String(formData.rows)}
                    onValueChange={(v) => {
                      const rows = parseInt(v);
                      setFormData(f => ({ ...f, rows, max_products: f.columns * rows }));
                    }}
                  >
                    <SelectTrigger id="vit-rows" data-testid="vitrine-form-rows">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map(n => (
                        <SelectItem key={n} value={String(n)}>{n} ligne{n > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground -mt-2 ml-1">
                Affichage : {formData.columns} × {formData.rows} = {formData.columns * formData.rows} produits max
              </p>

              {/* Products */}
              <div className="space-y-1.5">
                <Label className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-gray-400" />
                  Produits ({formData.product_ids.length}/{formData.max_products})
                </Label>
                <ProductSelector
                  selectedIds={formData.product_ids}
                  onAdd={(id) => {
                    if (formData.product_ids.length >= formData.max_products) {
                      toast.error(`Maximum ${formData.max_products} produits`);
                      return;
                    }
                    setFormData(f => ({ ...f, product_ids: [...f.product_ids, id] }));
                  }}
                  onRemove={(id) => setFormData(f => ({ ...f, product_ids: f.product_ids.filter(p => p !== id) }))}
                  onReorder={(newIds) => setFormData(f => ({ ...f, product_ids: newIds }))}
                />
              </div>

              {/* ── CONFLICT GUARD PANEL ── */}
              {conflicts.conflicts.length > 0 && (
                <div className="space-y-2" data-testid="vitrine-conflict-panel">
                  {conflicts.conflicts.map((c, idx) => (
                    <div key={idx} className={`flex items-start gap-2.5 p-3 rounded-lg border text-sm ${
                      c.level === 'critical' ? 'bg-red-50 border-red-200 text-red-800' :
                      c.level === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' :
                      'bg-blue-50 border-blue-200 text-blue-700'
                    }`} data-testid={`vitrine-conflict-${c.code}`}>
                      {c.level === 'critical' ? <ShieldAlert className="h-4 w-4 flex-shrink-0 mt-0.5" /> :
                       c.level === 'warning' ? <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" /> :
                       <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />}
                      <div className="flex-1 min-w-0">
                        <p>{c.message}</p>
                        {c.affected_vitrines?.length > 0 && (
                          <p className="text-xs mt-1 opacity-75">
                            Vitrine(s) concernée(s) : {c.affected_vitrines.map(v => `"${v.title}"`).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>
                  Annuler
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving || conflicts.status === 'error'}
                  className={conflicts.status === 'error' ? 'opacity-50 cursor-not-allowed' : ''}
                  data-testid="vitrine-form-save"
                >
                  {saving ? 'Enregistrement...' : (editingVitrine ? 'Mettre à jour' : 'Créer la vitrine')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* DELETE CONFIRM */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Supprimer la vitrine ?</DialogTitle>
              <DialogDescription>
                Cette action est irréversible. La vitrine et ses associations seront supprimées.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Annuler</Button>
              <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)} data-testid="vitrine-confirm-delete">
                Supprimer
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardWrapper>
  );
};

export default AdminVitrines;
